/* eslint-disable @next/next/no-img-element */
import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import axios, { Canceler } from 'axios';
import useCancel from '../../Hooks/CancelFetch';
import Backend from '../../../utils/Backend';
import { validateFormRequest, validateTradeinVehicle } from '../../../helper/validators';
import { toast } from 'react-toastify';
import { event, vehicleEnquiryBOIDict, vehicleEnquiryDict } from '../../../lib/gtag';
import { formatNumber } from '../../../helper/number_formatter';

import Loader from '../../Loader';
import HorizontalRadioButtons from '../../HorizontalRadioButtons';

import IOption from '../../../interfaces/IOption';

import styles from '../styles.module.scss'
import enquiryStyles from './styles.module.scss'
import BankOfIrelandLogo from '../../../public/assets/icons/bank-of-ireland-blue.png'
import { useIsBoiWebsite } from "../../Context/WebsiteType";
        
const yesNoOptions: IOption[] = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
]
const metricOptions: IOption[] = [
  { value: "km", label: "Km" },
  { value: "miles", label: "Miles" },
]

interface IForm {
  header: string, 
  text: string,
  submittedText: string,
  
  vehicle_id: number,
  dealership_id: number,
  action: string,
  callback?: () => void,
  
  // Sweep needs
  sweep?: boolean,
  vehicle?: any, // to pass sweep vehicle

  customDealership?: string,
  formType?: string,

  financeForm?: boolean,
}

export default function VehicleEnquiryForm({ 
  header, text, submittedText, formType = 'vehicle_enquiry',
  vehicle_id, dealership_id, action, callback, sweep, vehicle,
  customDealership, financeForm
} : IForm) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [phone, setPhone] = useState<string>("");

  // selectors
  const [tradeIn, setTradeIn] = useState<IOption>(yesNoOptions[1]);
  const [financeApproval, setFinanceApproval] = useState<IOption>(yesNoOptions[1]);
  const [requireFinance, setRequireFinance] = useState<IOption>(yesNoOptions[1]);

  // tradein fields
  const [regNumber, setRegNumber] = useState("");
  const [metricType, setMetricType] = useState<IOption>(metricOptions[0]);
  const [mileage, setMileage] = useState<number | string>('');
  const [condition, setCondition] = useState("");

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const router = useRouter();

  const dataHolderRef = useRef<HTMLDivElement>(null);

  const submitCancelRef = useRef<Canceler>()

  const isBoiWebsite = useIsBoiWebsite();

  useCancel(submitCancelRef.current)
  const sendRequest = () => {
    if (!validateFormRequest(name, email, message, phone, toast.error, (action === "QUESTION" || action === "QUESTION_BOI"))) return

    if (tradeIn.value === "yes") {
      if (!validateTradeinVehicle(regNumber, mileage, condition, toast.error)) {
        return
      }
    }

    setLoading(true);

    const vehicle_data = (!sweep || !vehicle) ? undefined : {
      vehicle_id: vehicle_id,
      vehicle_name: `${vehicle.model.manufacturer.name} ${vehicle.model.name}`,
      vehicle_image: vehicle.featured_image.original,
      dealership_id: parseInt(vehicle.dealer.nevo_dealer_id || -1),
      vehicle_registration: vehicle.registration_number || 'None',

      price: vehicle.price / 100,
      year: vehicle.year,
      make: vehicle.model.manufacturer.name,
      model: vehicle.model.name,
    }

    const tradeinVehicle = tradeIn.value === "yes" ? {
      registration: regNumber,
      mileage: `${mileage} ${metricType.value}`,
      condition: condition,
      source: isBoiWebsite ? 'BOI' : 'NEVO'
    } : undefined

    const { promise, cancel } = Backend.formRequest(
      name, email, message, formType,
      action, phone, vehicle_id, parseInt(`${dealership_id}`),
      financeApproval.value === 'yes',
      requireFinance.value === 'yes',
      tradeinVehicle,
      vehicle_data, 
      customDealership, router.query?.ad_type?.toString()
    )
    
    submitCancelRef.current = cancel;

    promise.then((res) => {
      if (res.status === 201) {
        if (dataHolderRef && dataHolderRef.current) {
          dataHolderRef.current.classList.toggle(styles.disappear);
        }
        setTimeout(() => {
          if (callback) { // vehicle form
            event(isBoiWebsite ? vehicleEnquiryBOIDict[action] : vehicleEnquiryDict[action])

            callback();
          }
          else setSubmitted(true);
        }, 750)
      }
    })
    .catch((err) => {
      if (axios.isCancel(err)) return;
      
      toast.error("Internal server error occured. Please, try again later.")
      setLoading(false)
    }); 
  }

  const onMileageInput = (e: any) => {
    let value: string = e.target.value;
    if (value === '') {
      setMileage(value)
      return
    }

    value = value.replaceAll(',', '')

    const parsedValue = parseInt(value)
    if (isNaN(parsedValue)) return

    if (parsedValue > 100_000_00) return;

    setMileage(formatNumber(parsedValue))
  }

  const onAskNewQuestion = () => {
    setMessage("");
    setSubmitted(false);
    setLoading(false);
  }

  useEffect(() => {
    onAskNewQuestion();
  }, [action])

  return (
    <div className={`${styles.formHolder} ${styles.vehicleForm} ${financeForm && styles.financeForm}`}>
      { financeForm && 
        <img className={enquiryStyles.bankLogo} src={BankOfIrelandLogo.src} alt='Bank of Ireland'/>
      }

      <h1 className={styles.header}>{ header }</h1>
      <p className={styles.subHeader}>{ submitted ? submittedText : text}</p>
      
      {
        !submitted &&
        <div className={styles.dataHolder} ref={dataHolderRef}>
          
          <div className={`${styles.input} transparent-input`}>
            <div>
              <input 
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Your Name *"
              />
            </div>
          </div>

          <div className={`${styles.input} transparent-input`}>
            <div>
              <input 
                type="text"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Email *"
              />
            </div>
          </div>

          <div className={`${styles.input} transparent-input`}>
            <div>
              <input 
                type="text"
                value={phone}
                onChange={e => {
                  setPhone(e.target.value.replaceAll(' ', '').replaceAll('(', '').replaceAll(')', '').replaceAll('-', ''))
                }}
                placeholder='Phone *'
              />
            </div>
          </div>

          { action === "QUESTION" || action === "QUESTION_BOI" &&
            <div className={`${styles.input} transparent-input`}>
              <div>
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder="Request *"
                  maxLength={1024}
                />
              </div>
            </div>
          }

          <div className={`${enquiryStyles.selectorsHolder}`}>
            <HorizontalRadioButtons
              options={yesNoOptions}
              selected={tradeIn}
              setSelected={setTradeIn}
              text='Have got a trade-in?'
              className={`${enquiryStyles.typeHolder}`}
            />

            { !financeForm && 
              <>
                <HorizontalRadioButtons
                  options={yesNoOptions}
                  selected={financeApproval}
                  setSelected={setFinanceApproval}
                  text='Approved for Finance?'
                  className={`${enquiryStyles.typeHolder}`}
                />

                <HorizontalRadioButtons
                  options={yesNoOptions}
                  selected={requireFinance}
                  setSelected={setRequireFinance}
                  text='Require Finance?'
                  className={`${enquiryStyles.typeHolder}`}
                />
              </>
            }
          </div>

          { tradeIn.value === "yes" && (
            <>
              <div className={`${styles.input} transparent-input`}>
                <div>
                  <input 
                    type="text"
                    value={regNumber}
                    onChange={e => setRegNumber(e.target.value)}
                    placeholder="Registration Number *"
                  />
                </div>
              </div>

              <HorizontalRadioButtons
                options={metricOptions}
                selected={metricType}
                setSelected={setMetricType}
                text='Mileage'
                className={`${enquiryStyles.typeHolder} ${styles.radioButtons}`}
              />

              <div className={`${styles.input} transparent-input`}>
                <div>
                  <input 
                    type="text"
                    value={mileage}
                    onChange={onMileageInput}
                    placeholder={`10,000 ${metricType.value} *`}
                  />
                </div>
              </div>

              <div className={`${styles.input} transparent-input`}>
                <div>
                  <textarea 
                    value={condition}
                    onChange={e => setCondition(e.target.value)}
                    placeholder="Vehicle Condition *"
                    maxLength={1024}
                  />
                </div>
              </div>
            </>
          )}

          <div className={styles.submitHolder}>
            { loading ? 
              <Loader /> :
              <button className={`${isBoiWebsite ? 'light-blue-button-boi' : 'blue-button'}`} onClick={sendRequest}>Send</button>
            }
          </div>
        </div>  
      }

      {submitted && (
        <button className={`${styles.newQuestionButton} ${isBoiWebsite ? 'light-blue-button-boi' : 'blue-button'}`} onClick={onAskNewQuestion}>
          Ask Another Question
        </button>
      )}
    </div>
  )
};
